import { useEffect, useMemo, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const makeGroupCode = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9_-]+/g, "_")
  .replace(/^_+|_+$/g, "");

export default function PickupGroups() {
  const { user, tenants: authTenants = [], activeTenantId } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [ramais, setRamais] = useState([]);

  useEffect(() => {
    api.get("/ramais", isSuperAdmin ? { skipTenantScope: true } : {
      headers: activeTenantId ? { "x-active-tenant-id": activeTenantId } : undefined,
    })
      .then(({ data }) => setRamais(Array.isArray(data) ? data : []))
      .catch(() => setRamais([]));
  }, [activeTenantId, isSuperAdmin, user?.tenant_id]);

  const tenants = Array.isArray(authTenants) ? authTenants : [];

  const tenantMap = useMemo(
    () => Object.fromEntries((tenants || []).map((tenant) => [tenant.id, tenant.nome])),
    [tenants],
  );

  const tenantMatches = (ramal, tenantId) => {
    const scopedTenantId = String(tenantId || user?.tenant_id || "").trim();
    if (!scopedTenantId) return true;
    const candidates = [
      ramal?.tenant_id,
      ramal?.tenantId,
      ramal?.tenant?.id,
      ramal?.tenant?.tenant_id,
    ]
      .filter(Boolean)
      .map((value) => String(value).trim());
    return candidates.includes(scopedTenantId);
  };

  const membersForGroup = (row) => {
    const linked = (ramais || []).filter(
      (ramal) => String(ramal.pickup_group_id || "") === String(row?.id || ""),
    );
    if (linked.length || !Array.isArray(row?.ramais)) return linked;
    return row.ramais.filter(Boolean).map((numero) => ({ numero, tenant_id: row?.tenant_id }));
  };

  const tenantLabelForGroup = (row, members) => {
    if (row?.tenant_name) return row.tenant_name;
    if (tenantMap[row?.tenant_id]) return tenantMap[row.tenant_id];
    const memberTenantId = members.find((ramal) => ramal?.tenant_id)?.tenant_id;
    if (memberTenantId && tenantMap[memberTenantId]) return tenantMap[memberTenantId];
    const fallbackTenant = (tenants || []).find((tenant) => {
      const tenantId = String(tenant?.id || "").trim();
      const rowTenantId = String(row?.tenant_id || "").trim();
      return tenantId && rowTenantId && tenantId === rowTenantId;
    });
    return fallbackTenant?.nome || row?.tenant_id || "-";
  };

  return (
    <CrudPage
      title="Grupo de captura"
      subtitle="Ramais do mesmo grupo capturam chamadas discando **"
      endpoint="/pickup-groups"
      testidPrefix="pickup-groups"
      headers={[...(isSuperAdmin ? ["Cliente"] : []), "Nome", "Identificador interno", "Codigo para capturar", "Ramais vinculados", "Status"]}
      defaultValues={{
        tenant_id: user?.tenant_id || "",
        nome: "",
        codigo: "",
        descricao: "",
        ramais: [],
        enabled: true,
      }}
      transformFormOpen={(form) => ({
        ...form,
        ramais: Array.isArray(form.ramais)
          ? form.ramais
          : membersForGroup(form).map((ramal) => ramal.numero),
      })}
      transformSubmit={(form) => ({
        ...form,
        codigo: form.codigo || makeGroupCode(form.nome) || `grupo_${Date.now()}`,
        ramais: Array.isArray(form.ramais) ? form.ramais : [],
      })}
      renderRow={(row) => {
        const members = membersForGroup(row);
        return (
          <>
            {isSuperAdmin && <td>{tenantLabelForGroup(row, members)}</td>}
            <td>
              <div className="font-medium">{row.nome}</div>
              <div className="text-[10px] text-[color:var(--text-muted)]">{row.descricao || "-"}</div>
            </td>
            <td className="font-mono">{row.codigo}</td>
            <td className="font-mono font-semibold">**</td>
            <td className="font-mono text-xs">
              {members.length ? members.map((ramal) => ramal.numero).join(", ") : "Nenhum"}
            </td>
            <td>
              {row.enabled ? (
                <span className="sbc-tag tag-online">ativo</span>
              ) : (
                <span className="sbc-tag tag-offline">inativo</span>
              )}
            </td>
          </>
        );
      }}
      fields={[
        ...(isSuperAdmin
          ? [
              {
                key: "tenant_id",
                label: "Cliente do grupo",
                type: "select",
                full: true,
                options: [
                  { value: "", label: "-- selecione o cliente --" },
                  ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.nome })),
                ],
                onChange: (next, tenantId, currentForm) => ({
                  ...next,
                  ramais: String(tenantId || "") === String(currentForm?.tenant_id || "")
                    ? (Array.isArray(currentForm?.ramais) ? currentForm.ramais : [])
                    : [],
                }),
              },
            ]
          : []),
        { key: "nome", label: "Nome do grupo", placeholder: "Ex: Comercial" },
        {
          key: "descricao",
          label: "Descricao",
          type: "textarea",
          full: true,
          placeholder: "Ramais que podem capturar chamadas entre si",
        },
        {
          key: "ramais",
          label: "Ramais vinculados",
          type: "checkbox-list",
          full: true,
          options: (form) =>
            (ramais || [])
              .filter((ramal) => ramal.enabled !== false && tenantMatches(ramal, form.tenant_id))
              .map((ramal) => ({
                value: ramal.numero,
                label: `${ramal.numero} - ${ramal.nome || "Sem nome"}`,
                hint: ramal.branch_name || ramal.caller_id_externo || ramal.caller_id || "Ramal interno",
              })),
          hint: "Ao escolher o cliente, a lista mostra somente os ramais dele. Para capturar uma chamada deste grupo, disque **.",
        },
        { key: "enabled", label: "Ativo", type: "toggle" },
      ]}
    />
  );
}
